/**
 * Scripture data layer. All scripture data reads go through this module.
 * UI code must not call supabase-js directly for scripture_topics or
 * scripture_topic_verses tables.
 */

import type { ParsedPrayer } from '@/types/prayer';
import type {
  ScriptureBookContext,
  ScriptureChapterContext,
  ScriptureTopic,
  ScriptureTopicVerse,
  ScriptureTopicVerseGroup,
  SpineProseMatch,
} from '@/types/scripture';
import { CANONICAL_BOOKS } from '@/lib/bsb-types';
import { getSupabase } from '@/lib/supabase';
import i18n from '@/i18n';

const SUGGEST_TOPICS_URL = '/api/grok-suggest-topics';

// Hardcoded for chunk M.3.7.a. Only the en-US slice of the curated
// taxonomy ships at launch; the schema's locale column is forward-
// compatible but no es-MX or pt-BR rows are seeded yet. The picker's
// 2-letter i18n.language is not mapped here because every locale falls
// back to en-US until the taxonomy is multi-locale.
//
// TODO: switch to locale routing when es-MX or pt-BR taxonomy rows are seeded
const TAXONOMY_LOCALE = 'en-US';

// --- private helpers -------------------------------------------------------

/**
 * Build the standard authenticated-API headers. Pattern copied (not
 * imported) from src/lib/grok.ts:21-31. Owning a local copy keeps this
 * module self-contained and avoids re-exporting from grok.ts.
 */
async function apiHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const sb = getSupabase();
  if (sb) {
    const { data } = await sb.auth.getSession();
    if (data.session?.access_token) {
      h['Authorization'] = `Bearer ${data.session.access_token}`;
    }
  }
  return h;
}

interface ScriptureTopicRow {
  id: string;
  slug: string;
  locale: string;
  name: string;
  description: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ScriptureTopicVerseRow {
  id: string;
  topic_id: string;
  book: string;
  chapter: number;
  verse_start: number;
  verse_end: number;
  reference_text: string;
  verse_text: string;
  translation: string;
  curator_note: string | null;
  verse_context: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ScriptureBookContextRow {
  book_slug: string;
  book_name: string;
  locale: string;
  book_context: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ScriptureChapterContextRow {
  book_slug: string;
  book_name: string;
  chapter: number;
  locale: string;
  chapter_context: string;
  created_at: string;
  updated_at: string;
}

function topicFromRow(row: ScriptureTopicRow): ScriptureTopic {
  return {
    id: row.id,
    slug: row.slug,
    locale: row.locale,
    name: row.name,
    description: row.description,
    displayOrder: row.display_order,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// bookContext is attached in a second pass by fetchBookContextsForVerses +
// attachBookContexts; verseFromRow returns null here so the row-shape
// transform stays a pure single-row operation.
function verseFromRow(row: ScriptureTopicVerseRow): ScriptureTopicVerse {
  return {
    id: row.id,
    topicId: row.topic_id,
    book: row.book,
    chapter: row.chapter,
    verseStart: row.verse_start,
    verseEnd: row.verse_end,
    referenceText: row.reference_text,
    verseText: row.verse_text,
    translation: row.translation,
    curatorNote: row.curator_note,
    verseContext: row.verse_context,
    bookContext: null,
    displayOrder: row.display_order,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function bookContextFromRow(row: ScriptureBookContextRow): ScriptureBookContext {
  return {
    bookSlug: row.book_slug,
    bookName: row.book_name,
    locale: row.locale,
    bookContext: row.book_context,
    displayOrder: row.display_order,
  };
}

function chapterContextFromRow(row: ScriptureChapterContextRow): ScriptureChapterContext {
  return {
    bookSlug: row.book_slug,
    bookName: row.book_name,
    chapter: row.chapter,
    locale: row.locale,
    chapterContext: row.chapter_context,
  };
}

/**
 * Single string transform used at both fetch time and (future) custom-verse
 * search time, mirroring the canonical-name guarantee enforced by the
 * importer in scripts/import-scripture-taxonomy.mjs. "Psalms" arrives
 * from book-context.md and normalizes to "Psalm" to match the
 * scripture_topic_verses.book column populated by the taxonomy import.
 * Exported so consumers outside this module (custom-verse search in
 * .b.2.d, future M.3.7.c) can reuse without duplicating the rule.
 */
export function normalizeBookName(name: string): string {
  return name === 'Psalms' ? 'Psalm' : name;
}

/**
 * Fetch ScriptureBookContext rows for the unique normalized book slugs
 * referenced by the given verses, keyed by bookSlug. Returns an empty
 * Map when the verses array contains no books. Throws on missing
 * Supabase client or DB error so callers can choose their failure
 * posture (getVersesByTopicSlugs rethrows; getVersesForTopic catches
 * and falls back to bookContext: null on every row).
 *
 * Privacy: error.message is logged to the console but NOT included in
 * the thrown Error string, matching the existing scripture.ts pattern.
 */
async function fetchBookContextsForVerses(
  verses: ScriptureTopicVerse[],
): Promise<Map<string, ScriptureBookContext>> {
  const uniqueSlugs = new Set<string>();
  for (const v of verses) {
    uniqueSlugs.add(normalizeBookName(v.book));
  }
  const matched = new Map<string, ScriptureBookContext>();
  if (uniqueSlugs.size === 0) return matched;

  const sb = getSupabase();
  if (!sb) throw new Error('book-context-supabase-missing');

  const { data, error } = await sb
    .from('scripture_book_contexts')
    .select('*')
    .in('book_slug', [...uniqueSlugs])
    .eq('locale', TAXONOMY_LOCALE);

  if (error) {
    console.error('[scripture] fetchBookContextsForVerses failed:', error.message);
    throw new Error('book-context-db-error');
  }

  const rows = (data ?? []) as ScriptureBookContextRow[];
  for (const row of rows) {
    const ctx = bookContextFromRow(row);
    matched.set(ctx.bookSlug, ctx);
  }
  console.info(
    '[scripture] book contexts ok:',
    `${matched.size}/${uniqueSlugs.size}`,
  );
  if (matched.size < uniqueSlugs.size) {
    const missing: string[] = [];
    for (const slug of uniqueSlugs) {
      if (!matched.has(slug)) missing.push(slug);
    }
    console.warn('[scripture] book contexts partial:', missing);
  }
  return matched;
}

/**
 * Fetch a single ScriptureBookContext row by book name + locale, or null
 * on miss. Sibling helper to fetchBookContextsForVerses; the verse-list
 * pipeline batches by slug set, the chapter reader looks up exactly one
 * book at a time. Sharing fetchBookContextsForVerses would force a
 * synthetic single-element verse array; a direct single-row fetch is
 * clearer and avoids that contortion.
 *
 * Locale defaults to TAXONOMY_LOCALE (en-US) so callers in the verse-
 * list pipeline (which never pass an explicit locale today) get the
 * same posture as fetchBookContextsForVerses. Callers in the chapter
 * reader pass the active user locale so an es-MX or pt-BR user can be
 * surfaced their localized row when the taxonomy ships those locales.
 * Until then, a miss returns null and the caller renders the localized
 * empty state.
 *
 * Throws 'book-context-supabase-missing' when no Supabase client.
 * Throws 'book-context-db-error' on DB error (error.message logged to
 * console.error for ops visibility, NOT included in the thrown Error
 * string, matching the existing scripture.ts privacy posture).
 */
export async function fetchBookContext(
  bookName: string,
  locale?: string,
): Promise<ScriptureBookContext | null> {
  const slug = normalizeBookName(bookName);
  const targetLocale = locale ?? TAXONOMY_LOCALE;

  const sb = getSupabase();
  if (!sb) throw new Error('book-context-supabase-missing');

  const { data, error } = await sb
    .from('scripture_book_contexts')
    .select('*')
    .eq('book_slug', slug)
    .eq('locale', targetLocale)
    .maybeSingle();

  if (error) {
    console.error('[scripture] fetchBookContext failed:', error.message);
    throw new Error('book-context-db-error');
  }

  if (!data) return null;
  return bookContextFromRow(data as ScriptureBookContextRow);
}

/**
 * Fetch a single ScriptureChapterContext row by book name + chapter +
 * locale, or null on miss. Sibling helper to fetchBookContext; the
 * chapter view looks up exactly one row at a time per chapter visit.
 *
 * Locale defaults to TAXONOMY_LOCALE (en-US) so callers in surfaces
 * that have not yet adopted per-locale routing get the same posture
 * as fetchBookContext. Callers in the chapter view pass the active
 * user locale so an es-MX or pt-BR user can be surfaced their
 * localized row when the taxonomy ships those locales.
 *
 * Throws 'chapter-context-supabase-missing' when no Supabase client.
 * Throws 'chapter-context-db-error' on DB error (error.message logged
 * to console.error for ops visibility, NOT included in the thrown
 * Error string, matching the existing scripture.ts privacy posture).
 *
 * Exported but unused in M.3.8.f.2.a. Consumed by f.2.b UI wiring.
 */
export async function fetchChapterContext(
  bookName: string,
  chapter: number,
  locale?: string,
): Promise<ScriptureChapterContext | null> {
  const slug = normalizeBookName(bookName);
  const targetLocale = locale ?? TAXONOMY_LOCALE;

  const sb = getSupabase();
  if (!sb) throw new Error('chapter-context-supabase-missing');

  const { data, error } = await sb
    .from('scripture_chapter_contexts')
    .select('*')
    .eq('book_slug', slug)
    .eq('chapter', chapter)
    .eq('locale', targetLocale)
    .maybeSingle();

  if (error) {
    console.error('[scripture] fetchChapterContext failed:', error.message);
    throw new Error('chapter-context-db-error');
  }

  if (!data) return null;
  return chapterContextFromRow(data as ScriptureChapterContextRow);
}

/**
 * Pure transform: return a new verses array with bookContext attached
 * by matching each verse's normalized book name against the Map. When
 * contextsBySlug is empty (book-context fetch failed in the
 * getVersesForTopic best-effort path), every returned verse carries
 * bookContext: null.
 */
function attachBookContexts(
  verses: ScriptureTopicVerse[],
  contextsBySlug: Map<string, ScriptureBookContext>,
): ScriptureTopicVerse[] {
  return verses.map((v) => ({
    ...v,
    bookContext: contextsBySlug.get(normalizeBookName(v.book)) ?? null,
  }));
}

// Boot-time diagnostic: surface missing Supabase client once per session
// so missing-env-var failures are observable in the browser console.
// Logs status bucket only; no env values or keys. Module top-level scope
// so this fires exactly once per page load (M.3.7.b.1.d).
if (typeof window !== 'undefined') {
  const sbBootStatus = getSupabase() ? 'ok' : 'missing';
  console.info('[scripture] supabase client:', sbBootStatus);
}

// --- public API ------------------------------------------------------------

/**
 * Classify a parsed prayer into curated topic slugs via the
 * /api/grok-suggest-topics endpoint. Throws on real failures
 * (network/abort, non-OK HTTP, JSON parse failure, shape-invalid
 * response). A successful 200 response with empty slugs is NOT a
 * failure: Grok genuinely returning "no matching topics" returns
 * []. Picker Effect 3's catch routes thrown errors to the
 * suggestionState 'failed' inline error region; legit-empty stays
 * a non-error empty result. Privacy: thrown Error message strings
 * carry only failure-category buckets; no prayer text, no who, no
 * env values, no upstream response body.
 */
export async function suggestTopicSlugs(
  parsed: ParsedPrayer,
): Promise<string[]> {
  const body: Record<string, unknown> = {
    core_prayer: parsed.core_prayer,
    language: (i18n.language ?? 'en').slice(0, 2),
  };
  if (parsed.who) body.who = parsed.who;
  if (parsed.mentionedPeople && parsed.mentionedPeople.length > 0) {
    body.mentionedPeople = parsed.mentionedPeople;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  let res: Response;
  try {
    res = await fetch(SUGGEST_TOPICS_URL, {
      method: 'POST',
      headers: await apiHeaders(),
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    console.error('[scripture] topic suggestion failed:', err);
    throw new Error('suggestion-network-failure');
  }
  clearTimeout(timeout);

  if (!res.ok) {
    console.error('[scripture] topic suggestion failed:', res.status);
    throw new Error(`suggestion-http-${res.status}`);
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch (err) {
    console.error('[scripture] topic suggestion failed:', err);
    throw new Error('suggestion-json-parse-failure');
  }
  if (!data || typeof data !== 'object') {
    throw new Error('suggestion-shape-invalid');
  }
  const slugs = (data as { slugs?: unknown }).slugs;
  if (!Array.isArray(slugs)) {
    throw new Error('suggestion-shape-invalid');
  }
  const filtered = slugs.filter((s): s is string => typeof s === 'string');
  console.info('[scripture] suggestion ok:', filtered.length, 'slugs');
  return filtered;
}

/**
 * Fetch ScriptureTopic rows for the given slugs, returned in the
 * caller-provided slug order. Slugs not present in the curated set
 * for TAXONOMY_LOCALE are dropped silently.
 */
export async function getTopicsBySlugs(
  slugs: string[],
): Promise<ScriptureTopic[]> {
  if (slugs.length === 0) return [];
  const sb = getSupabase();
  if (!sb) return [];

  const { data, error } = await sb
    .from('scripture_topics')
    .select('*')
    .in('slug', slugs)
    .eq('locale', TAXONOMY_LOCALE)
    .eq('is_active', true);

  if (error) {
    console.error('[scripture] getTopicsBySlugs failed:', error.message);
    return [];
  }
  const rows = (data ?? []) as ScriptureTopicRow[];
  const bySlug = new Map<string, ScriptureTopic>();
  for (const row of rows) {
    bySlug.set(row.slug, topicFromRow(row));
  }
  const out: ScriptureTopic[] = [];
  for (const slug of slugs) {
    const topic = bySlug.get(slug);
    if (topic) out.push(topic);
  }
  return out;
}

/**
 * Fetch ScriptureTopic + active verses for each slug, returned in the
 * caller-provided slug order. Uses a single PostgREST embed query.
 * Inactive verses are filtered server-side via the embedded-relation
 * predicate; inactive topics are filtered via the parent predicate.
 *
 * Contract: empty slugs input returns [] (legit empty, no error).
 * Missing Supabase client throws 'verses-supabase-missing'. DB error
 * throws 'verses-db-error' (error.message stays in the console.error
 * for ops visibility but is NOT included in the thrown Error string,
 * since that string surfaces in the picker's catch and we don't want
 * server-side details leaking). Non-empty slugs producing zero matched
 * groups throws 'verses-zero-groups' (advisor framing: this is RLS
 * denial or slug-prompt-DB drift, not legitimate empty).
 */
export async function getVersesByTopicSlugs(
  slugs: string[],
): Promise<ScriptureTopicVerseGroup[]> {
  if (slugs.length === 0) return [];
  const sb = getSupabase();
  if (!sb) throw new Error('verses-supabase-missing');

  // Single embed: parent predicate filters topics; embedded predicate
  // filters child verses. Verses are sorted at the relation level by
  // (display_order asc, reference_text asc) matching the picker index
  // shape from migration 015. Parent rows arrive in SQL default order;
  // we reorder client-side to match caller-provided slug order.
  const { data, error } = await sb
    .from('scripture_topics')
    .select('*, scripture_topic_verses(*)')
    .eq('locale', TAXONOMY_LOCALE)
    .eq('is_active', true)
    .in('slug', slugs)
    .eq('scripture_topic_verses.is_active', true)
    .order('display_order', {
      referencedTable: 'scripture_topic_verses',
      ascending: true,
    })
    .order('reference_text', {
      referencedTable: 'scripture_topic_verses',
      ascending: true,
    });

  if (error) {
    console.error('[scripture] getVersesByTopicSlugs failed:', error.message);
    throw new Error('verses-db-error');
  }

  type EmbedRow = ScriptureTopicRow & {
    scripture_topic_verses: ScriptureTopicVerseRow[] | null;
  };
  const rows = (data ?? []) as EmbedRow[];
  const bySlug = new Map<string, ScriptureTopicVerseGroup>();
  for (const row of rows) {
    const verseRows = row.scripture_topic_verses ?? [];
    bySlug.set(row.slug, {
      topic: topicFromRow(row),
      verses: verseRows.map(verseFromRow),
    });
  }
  const out: ScriptureTopicVerseGroup[] = [];
  for (const slug of slugs) {
    const group = bySlug.get(slug);
    if (group) out.push(group);
  }
  if (out.length === 0) {
    console.warn('[scripture] non-empty slugs but zero groups');
    throw new Error('verses-zero-groups');
  }
  const totalVerses = out.reduce((n, g) => n + g.verses.length, 0);
  console.info('[scripture] verses ok:', out.length, 'groups,', totalVerses, 'verses');

  // Attach book contexts in a second pass. Rethrow on failure to match
  // this function's existing throw posture so the picker's catch surfaces
  // the failure as a unified suggestion error rather than rendering verses
  // without their book context.
  const allVerses = out.flatMap((g) => g.verses);
  const contextsBySlug = await fetchBookContextsForVerses(allVerses);
  for (const g of out) {
    g.verses = attachBookContexts(g.verses, contextsBySlug);
  }
  return out;
}

/**
 * Fetch every active topic for TAXONOMY_LOCALE, ordered for picker
 * display by (display_order asc, name asc) matching the curated
 * ordering exported by the import script.
 */
export async function getAllActiveTopics(): Promise<ScriptureTopic[]> {
  const sb = getSupabase();
  if (!sb) return [];

  const { data, error } = await sb
    .from('scripture_topics')
    .select('*')
    .eq('locale', TAXONOMY_LOCALE)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.error('[scripture] getAllActiveTopics failed:', error.message);
    return [];
  }
  const rows = (data ?? []) as ScriptureTopicRow[];
  return rows.map(topicFromRow);
}

// --- Spine catalog prose lookup (DORMANT) ---------------------------------
//
// Single-verse lookup against the operator-curated spine catalog
// (CLAUDE.md rule #76, migration 018).
//
// What this was: the data-layer query that powered LetThemKnowSheet's
// Q3 ("Include the story behind this verse?") on the outbound share
// card flow (Ship 2a → 2a.3). After the user picked a verse on the
// share picker, this helper would look up whether the catalog had a
// matching curated story; if so, the sheet rendered display_prose as
// an optional add-on slot beneath the verse.
//
// What it did: two-tier read (Tier A named stories with primary_ref
// range overlap; Tier B chapter-PK fallback) with Tier A winning over
// Tier B per rule #76. The narrowest passage range wins within Tier A
// for deterministic tie-breaking. The safety filter (auto_pair_safe +
// auto_skipped) is enforced both server-side via PostgREST predicates
// AND defensively in the row-shape guards inside this helper, so
// unsafe rows (Job 4-8 friends' speeches, oracles-of-doom, ritual-
// instruction-only chapters, genealogies) carry display_prose strings
// in the catalog but never reach a caller.
//
// Why dormant: the LetThemKnowSheet Q3 consumer was retired on
// 2026-05-23 — operator pivot away from a story surface on share
// cards in favor of a simpler proof + verse + cardLabel composition.
// Two earlier alternatives (operator-curate per-verse summaries on the
// picker rows, or accept the chapter-recap content model with known
// issues) were both dropped in favor of removing the consumer
// entirely. After the retirement this helper has zero call sites in
// the app.
//
// Posture: kept dormant per CLAUDE.md rule #77 (which retired the
// earlier auto-push prayer→story matching engine while leaving the
// catalog tables, RPC, and dormant DB objects in place). Same pattern
// here — preserve the helper at the data-layer boundary; the share-
// card picker still emits the four structured SelectedVerse fields
// (`bookName` / `chapter` / `verseStart` / `verseEnd`) this helper
// needs as input, so any future pull-based discovery surface can
// revive without re-plumbing the producer side.
//
// Do NOT delete on a cleanup sweep. See CLAUDE.md "Banked code edits"
// for the dormant-spine-helper entry.
//
// When Ship 2b (or any future pull surface) revives this code path,
// the bulk variant of this helper MUST preserve both safety filter
// layers — unsafe rows must NEVER reach a card render.

interface SpineStoryRow {
  story_key: string;
  primary_ref: unknown; // jsonb — shape-guarded by verseOverlapsStoryRange
  narrative: unknown; // jsonb — narrative.display_prose extracted defensively
  auto_pair_safe: boolean;
}

interface SpineChapterRow {
  chapter: number;
  narrative: unknown;
  auto_pair_safe: boolean;
  auto_skipped: boolean;
}

interface StoryPrimaryRef {
  book: string;
  chapter_start: number;
  chapter_end: number;
  verse_start: number;
  verse_end: number;
}

// Module-scoped name → slug map built once from CANONICAL_BOOKS at module
// init. Picker verses expose `book: string` matching the canonical book
// name (e.g., "Psalm", "Song of Solomon", "1 Samuel"); the spine catalog
// uses route-style slugs (e.g., "psalm", "song-of-solomon", "1-samuel").
// CANONICAL_BOOKS is the single source of truth for the mapping.
const BOOK_NAME_TO_SLUG: Map<string, string> = new Map(
  CANONICAL_BOOKS.map((entry) => [entry.name, entry.slug]),
);

function isNonEmptyTrimmedString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFiniteInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Extract `display_prose` from a catalog row's `narrative` jsonb. Returns
 * a trimmed string or null when the field is missing, non-string, or
 * empty after trimming. The trimmed-non-empty invariant is what lets
 * callers safely use `match.displayProse.length > 0` downstream.
 */
function extractDisplayProse(narrative: unknown): string | null {
  if (typeof narrative !== 'object' || narrative === null) return null;
  const prose = (narrative as { display_prose?: unknown }).display_prose;
  if (!isNonEmptyTrimmedString(prose)) return null;
  return prose.trim();
}

/**
 * Pure helper: does a picker verse fall within a Tier A story's
 * primary_ref range? Runtime-guards the jsonb shape and rejects
 * malformed ranges. Book equality is NOT compared here — the SQL
 * predicate `.eq('book_slug', slug)` is the authoritative book gate, so
 * primary_ref.book is shape-validated (must be a string) but not
 * compared. The catalog stores primary_ref.book as a canonical NAME
 * ("Genesis"), not the slug ("genesis"); requiring a slug comparison
 * here would never match.
 *
 * Overlap logic for a single-verse pick (verseStart === verseEnd in the
 * common case; range only for verse_start..verse_end style refs):
 *   - middle of multi-chapter span → always overlaps
 *   - same chapter as both start AND end → standard interval overlap
 *   - chapter === chapter_start only → verseEnd >= primary_ref.verse_start
 *   - chapter === chapter_end only → verseStart <= primary_ref.verse_end
 *   - else → no overlap
 */
function verseOverlapsStoryRange(
  verse: { chapter: number; verseStart: number; verseEnd: number },
  primaryRef: unknown,
): primaryRef is StoryPrimaryRef {
  if (typeof primaryRef !== 'object' || primaryRef === null) return false;
  const ref = primaryRef as Record<string, unknown>;
  if (!isNonEmptyTrimmedString(ref.book)) return false;
  if (
    !isFiniteInteger(ref.chapter_start) ||
    !isFiniteInteger(ref.chapter_end) ||
    !isFiniteInteger(ref.verse_start) ||
    !isFiniteInteger(ref.verse_end)
  ) {
    return false;
  }
  // Reject malformed ranges in the catalog (defensive — validator
  // pipeline should catch these at curation time).
  if (ref.chapter_start > ref.chapter_end) return false;
  if (ref.chapter_start === ref.chapter_end && ref.verse_start > ref.verse_end) {
    return false;
  }

  const { chapter, verseStart, verseEnd } = verse;
  if (chapter > ref.chapter_start && chapter < ref.chapter_end) return true;
  if (chapter === ref.chapter_start && chapter === ref.chapter_end) {
    return verseEnd >= ref.verse_start && verseStart <= ref.verse_end;
  }
  if (chapter === ref.chapter_start) {
    return verseEnd >= ref.verse_start;
  }
  if (chapter === ref.chapter_end) {
    return verseStart <= ref.verse_end;
  }
  return false;
}

/**
 * Tier A tie-break: narrowest passage range wins, then story_key for
 * deterministic ordering. The chapter-delta dominates the score so a
 * multi-chapter span always loses to a single-chapter span; verse-range
 * tiebreaks within the same chapter.
 */
function tierAScore(ref: StoryPrimaryRef): number {
  const chapterSpan = ref.chapter_end - ref.chapter_start;
  const verseSpan = ref.verse_end - ref.verse_start;
  return chapterSpan * 200 + verseSpan;
}

/**
 * Look up the spine catalog for a story matching the given verse.
 * Tier A wins over Tier B; within Tier A the narrowest passage range
 * wins (story_key alphabetical tiebreak). Returns null when no safe
 * match exists for the verse (no Tier A overlap AND no Tier B chapter
 * row, OR all candidates were filtered by the safety gates).
 *
 * Safety filter is enforced server-side via PostgREST predicates AND
 * defensively in row-shape guards inside this helper (defense-in-depth
 * against future code paths that might reuse with relaxed predicates):
 *   - Tier A: auto_pair_safe = true
 *   - Tier B: auto_pair_safe = true AND auto_skipped = false
 *   - Both: narrative.display_prose is a non-empty trimmed string
 *
 * Returns null silently (logs to console) on any failure of the
 * Tier A or Tier B query — prose is an informational enhancement, not
 * a blocking concern. Throws only when Supabase client is unavailable.
 *
 * Ship 2a matches only `primary_ref`. `additional_refs` are NOT
 * considered — documented as intentional. Ship 2b may extend this.
 */
export async function fetchSpineProseForVerse(
  bookName: string,
  chapter: number,
  verseStart: number,
  verseEnd: number,
): Promise<SpineProseMatch | null> {
  const canonicalSlug = BOOK_NAME_TO_SLUG.get(normalizeBookName(bookName));
  if (!canonicalSlug) return null;
  if (!isFiniteInteger(chapter) || !isFiniteInteger(verseStart) || !isFiniteInteger(verseEnd)) {
    return null;
  }
  // Spine catalog stores Psalms as book_slug='psalms' (plural, matching
  // the book's display name on disk in scripts/spine-catalog/psalms.json);
  // the rest of the app uses 'psalm' (singular per rule #62 verse-
  // reference register, surfaced via BOOK_NAME_TO_SLUG). This is the
  // only cross-side asymmetry — 65 of 66 books match canonical exactly.
  // Normalize at the query boundary so picker picks against the catalog
  // resolve correctly. Returned match.bookSlug carries the catalog slug
  // ('psalms'), NOT the Bible route slug ('psalm').
  const slug = canonicalSlug === 'psalm' ? 'psalms' : canonicalSlug;

  const sb = getSupabase();
  if (!sb) throw new Error('spine-prose-supabase-missing');

  const verseKey = { chapter, verseStart, verseEnd };

  // Tier A: pull all safe stories for this book, then client-side
  // overlap-filter + narrowest-range tiebreak. Bounded volume (~5-15
  // stories per book).
  let tierAMatch: SpineProseMatch | null = null;
  try {
    const { data, error } = await sb
      .from('scripture_stories')
      .select('story_key, primary_ref, narrative, auto_pair_safe')
      .eq('book_slug', slug)
      .eq('auto_pair_safe', true);

    if (error) {
      console.error('[scripture] tier A fetch failed:', error.message);
    } else {
      const rows = (data ?? []) as SpineStoryRow[];
      const candidates: Array<{ row: SpineStoryRow; ref: StoryPrimaryRef; prose: string }> = [];
      for (const row of rows) {
        if (!row.auto_pair_safe) continue; // defense-in-depth
        if (!verseOverlapsStoryRange(verseKey, row.primary_ref)) continue;
        const prose = extractDisplayProse(row.narrative);
        if (!prose) continue;
        candidates.push({ row, ref: row.primary_ref as StoryPrimaryRef, prose });
      }
      if (candidates.length > 0) {
        candidates.sort((a, b) => {
          const scoreDiff = tierAScore(a.ref) - tierAScore(b.ref);
          if (scoreDiff !== 0) return scoreDiff;
          return a.row.story_key.localeCompare(b.row.story_key);
        });
        const winner = candidates[0];
        tierAMatch = {
          bookSlug: slug,
          chapter,
          verseStart,
          verseEnd,
          tier: 'A',
          displayProse: winner.prose,
          storyKey: winner.row.story_key,
        };
      }
    }
  } catch (err) {
    console.error('[scripture] tier A fetch threw:', err);
  }

  if (tierAMatch) return tierAMatch;

  // Tier B: chapter-PK direct hit, safe + not auto-skipped.
  try {
    const { data, error } = await sb
      .from('scripture_chapter_tags')
      .select('chapter, narrative, auto_pair_safe, auto_skipped')
      .eq('book_slug', slug)
      .eq('chapter', chapter)
      .eq('auto_pair_safe', true)
      .eq('auto_skipped', false)
      .maybeSingle();

    if (error) {
      console.error('[scripture] tier B fetch failed:', error.message);
      return null;
    }
    if (!data) return null;
    const row = data as SpineChapterRow;
    if (!row.auto_pair_safe || row.auto_skipped) return null; // defense-in-depth
    const prose = extractDisplayProse(row.narrative);
    if (!prose) return null;
    return {
      bookSlug: slug,
      chapter,
      verseStart,
      verseEnd,
      tier: 'B',
      displayProse: prose,
    };
  } catch (err) {
    console.error('[scripture] tier B fetch threw:', err);
    return null;
  }
}

/**
 * Fetch active verses for a single topic id, ordered for display by
 * (display_order asc, reference_text asc).
 */
export async function getVersesForTopic(
  topicId: string,
): Promise<ScriptureTopicVerse[]> {
  if (!topicId) return [];
  const sb = getSupabase();
  if (!sb) return [];

  const { data, error } = await sb
    .from('scripture_topic_verses')
    .select('*')
    .eq('topic_id', topicId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('reference_text', { ascending: true });

  if (error) {
    console.error('[scripture] getVersesForTopic failed:', error.message);
    return [];
  }
  const rows = (data ?? []) as ScriptureTopicVerseRow[];
  const verses = rows.map(verseFromRow);

  // Book-context fetch is best-effort here; this function's silent-
  // degradation contract returns verses even when downstream lookups
  // fail. A failure attaches bookContext: null on every row so the
  // picker can render verses without the context block until the
  // next retry.
  try {
    const contextsBySlug = await fetchBookContextsForVerses(verses);
    return attachBookContexts(verses, contextsBySlug);
  } catch (err) {
    console.error(
      '[scripture] book context fetch failed (verses-for-topic):',
      err instanceof Error ? err.message : 'unknown',
    );
    return attachBookContexts(verses, new Map());
  }
}
